import { motion } from "framer-motion";
import { useTranslation } from "react-i18next";
import { SUPPORTED_CHANNELS, PAYMENT_METHODS } from "@/lib/content";
import { SectionHeading } from "./SectionHeading";

const Monogram = ({ name }) => {
  const initials = name.replace(/[()]/g, "").split(/\s+/).map((w) => w[0]).join("").slice(0, 2).toUpperCase();
  return (
    <div className="grid h-8 w-8 place-items-center rounded-md bg-slate-900 text-[10px] font-extrabold text-white">
      {initials}
    </div>
  );
};

const ChannelLogo = ({ name, logo }) => {
  if (!logo) return <Monogram name={name} />;
  return (
    <img
      src={logo}
      alt={`${name} platform logo`}
      loading="lazy"
      decoding="async"
      width="32"
      height="32"
      referrerPolicy="no-referrer"
      onError={(e) => {
        const img = e.currentTarget;
        if (img.dataset.fb) return;
        img.dataset.fb = "1";
        const wrap = document.createElement("div");
        wrap.className = "grid h-8 w-8 place-items-center rounded-md bg-slate-900 text-[10px] font-extrabold text-white";
        wrap.textContent = name.replace(/[()]/g, "").split(/\s+/).map((w) => w[0]).join("").slice(0, 2).toUpperCase();
        img.replaceWith(wrap);
      }}
      className="h-8 w-8 object-contain"
    />
  );
};

export const SupportedChannelsSection = () => {
  const { t } = useTranslation();
  return (
    <section data-testid="supported-channels-section">
      <div className="mx-auto w-full max-w-6xl px-4 py-16 sm:px-6 sm:py-20 lg:px-8">
        <SectionHeading
          eyebrow={t("sections.creative.eyebrow")}
          title={t("sections.creative.title")}
          description={t("sections.creative.desc")}
          align="center"
          testIdPrefix="supported-channels"
        />
        <div className="mt-10 grid grid-cols-2 gap-3 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6">
          {SUPPORTED_CHANNELS.map((c, idx) => (
            <motion.div
              key={c.name}
              initial={{ opacity: 0, y: 8 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-40px" }}
              transition={{ duration: 0.25, delay: idx * 0.02 }}
              className="group flex flex-col items-center justify-center rounded-xl border border-slate-200 bg-white p-4 transition-all hover:border-slate-300 hover:shadow-[0_4px_16px_rgba(15,23,42,0.05)]"
              data-testid={`supported-channel-${c.name.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/-+$/g, "")}`}
            >
              <ChannelLogo name={c.name} logo={c.logo} />
              <div className="mt-3 text-sm font-bold text-slate-900">{c.name}</div>
              <div className="mt-0.5 text-[10px] font-semibold uppercase tracking-wider text-slate-400">{c.category}</div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};

export const PaymentMethodsSection = () => {
  const { t } = useTranslation();
  return (
    <section data-testid="payment-methods-section" className="bg-slate-50">
      <div className="mx-auto w-full max-w-6xl px-4 py-16 sm:px-6 sm:py-20 lg:px-8">
        <SectionHeading
          eyebrow={t("sections.payments.eyebrow")}
          title={t("sections.payments.title")}
          description={t("sections.payments.desc")}
          align="center"
          testIdPrefix="payments"
        />
        <div className="mx-auto mt-10 grid max-w-3xl gap-4 sm:grid-cols-2 lg:grid-cols-5">
          {PAYMENT_METHODS.map((p) => (
            <div
              key={p.tk}
              className="flex flex-col items-center justify-center rounded-xl border border-slate-200 bg-white p-5 text-center"
              data-testid={`payment-method-${p.tk.split(".").pop().toLowerCase()}`}
            >
              <div className="grid h-10 w-10 place-items-center rounded-lg bg-blue-50 text-blue-700"><p.icon className="h-5 w-5" /></div>
              <div className="mt-3 text-sm font-bold text-slate-900">{t(`content.${p.tk}`)}</div>
            </div>
          ))}
        </div>
        <p className="mx-auto mt-6 max-w-2xl text-center text-xs text-slate-500">
          {t("common.paymentNote")}
        </p>
      </div>
    </section>
  );
};
